import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import {
  EnrollmentPeriod,
  EnrollmentType,
  InputPerformedBy,
  InputProposalV2,
  ProductLine,
  ProductType,
  Proposal,
  ProposalAction,
} from 'types/enrollmentService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { Group } from 'types/groupService';
import { CopyHelper } from 'global/utils/CopyHelper';
import { INITIAL_GROUP_OBJECT } from 'store/domain/groupStore';
import { FetchResult } from '@apollo/client';
import { Cart } from 'types/cartServiceV2';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { ToastInfo } from 'types/general';
import {
  confirmCartItems as confirmCartItemsInEnrollmentService,
  deleteEnrollment,
  getAllEnrollmentPeriodsByGroupId,
  getAllInactiveEnrollmentPeriodsByGroupId,
  saveProposal as saveProposalInEnrollmentService,
  sellEnrollment,
} from 'api/operations/enrollmentService';
import { confirmCartItems as confirmCartItemsInCartService } from 'api/operations/cartServiceV2';
import { renewalProductLineName } from 'global/services-helpers/enrollmentService';

export interface DynamicEnrollmentCard extends EnrollmentPeriod {
  loading: boolean;
}

export interface ShortCurrentPeriodInfo {
  periodId: string;
  effectiveDate: string;
}

export const INITIAL_CURRENT_PERIOD = Object.freeze({ periodId: '', effectiveDate: '' });

export class EnrollmentCardStore {
  rootStore: RootStore;
  loading = false;
  /**
   * Trigger to update proposal details on the group info page
   */
  enrollmentTrigger = 0;
  activePeriods: DynamicEnrollmentCard[] = [];
  inactivePeriods: EnrollmentPeriod[] = [];
  group: Group;
  showChangeEffectiveDateModal = false;
  /**
   * Period where effective date will be changed
   */
  currentPeriod: ShortCurrentPeriodInfo = INITIAL_CURRENT_PERIOD;

  // Toast Info
  toastInfo: ToastInfo = {
    showToast: false,
    toastType: ToastColorTypes.SUCCESS,
    toastMessage: '',
  };

  private updatedEnrollmentTrigger(): void {
    this.enrollmentTrigger += 1;
  }

  private setLoadingStatus(status: boolean): void {
    this.loading = status;
  }

  public updateChangeEffectiveDateModalStatus(status: boolean, currentPeriod: ShortCurrentPeriodInfo): void {
    this.showChangeEffectiveDateModal = status;
    this.currentPeriod = currentPeriod;
  }

  public showToast(toastMessage: string, type: ToastColorTypes): void {
    this.resetToastInfo();
    this.toastInfo.showToast = true;
    this.toastInfo.toastMessage = toastMessage;
    this.toastInfo.toastType = type;
  }

  public closeToast(): void {
    this.toastInfo.showToast = false;
    this.resetToastInfo();
  }

  private resetToastInfo(): void {
    this.toastInfo.toastType = ToastColorTypes.SUCCESS;
    this.toastInfo.toastMessage = '';
  }

  public async collectEnrollmentPeriods(groupId: string): Promise<void> {
    this.setLoadingStatus(true);

    const requestsForPeriods = [
      getAllEnrollmentPeriodsByGroupId(groupId),
      getAllInactiveEnrollmentPeriodsByGroupId(groupId),
      this.rootStore.groupStore.getGroupById(groupId),
    ];

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const promisesResult = await Promise.all(requestsForPeriods);

    void runInAction(async () => {
      this.activePeriods = (promisesResult[0] as EnrollmentPeriod[]).map((period) => {
        return { ...period, loading: false };
      });
      this.inactivePeriods = (promisesResult[1] as EnrollmentPeriod[]).sort(
        (a, b) => +new Date(b.effectiveDate) - +new Date(a.effectiveDate),
      );
      this.group = this.rootStore.groupStore.group;

      this.setLoadingStatus(false);
      await this.createCartForDefaultRenewalProposal();
    });
  }

  public choosePlan(periodId: string, productLineId: ProductLineId, choice: ProductType): void {
    const productLine = this.getProductLineFromDefaultRenewalProposal(periodId, productLineId);
    const renewalConfirmedPortfolio = renewalProductLineName(this.rootStore.userStore.sponsorId, productLineId);
    productLine.confirmedPortfolioId = renewalConfirmedPortfolio;

    switch (choice) {
      case ProductType.RENEW: {
        productLine.productType = ProductType.RENEW;
        break;
      }
      case ProductType.CANCEL: {
        productLine.productType = ProductType.CANCEL;
        break;
      }
      default: {
        productLine.productType = ProductType.CHANGE_PLAN;
        break;
      }
    }
  }

  /**
   * This method applies only on the Renewal Enrollment Card,
   * and only when this Enrollment Period in Renewal status and don't have Confirmed Proposal
   * @param periodId
   */
  public async confirmRenewalProposal(periodId: string): Promise<void> {
    const period = this.getActivePeriod(periodId);
    const defaultProposal = this.getDefaultRenewalProposal(periodId);

    this.setLoadingsOnCard([period], true);

    const inputProposal: InputProposalV2 = {
      cartId: defaultProposal.cartId,
      productLines: defaultProposal.productLines,
      cartName: defaultProposal.name,
    };

    // Set always zero, because we don't have variants of the plan
    const portfolioIdToSelectIndex: Record<string, number> = {};
    defaultProposal.productLines
      .map((line) => line.confirmedPortfolioId)
      .filter((id) => !!id)
      // eslint-disable-next-line no-return-assign,@typescript-eslint/no-non-null-assertion
      .forEach((id) => (portfolioIdToSelectIndex[id!] = 0));

    const requests = [
      confirmCartItemsInCartService(defaultProposal.cartId, portfolioIdToSelectIndex),
      confirmCartItemsInEnrollmentService(period.id, inputProposal, ProposalAction.CONFIRM),
    ];
    await Promise.all(requests);
    runInAction(() => {
      this.setLoadingsOnCard([period], false);
    });
  }

  async sellEnrollment(periodId: string): Promise<void> {
    this.setLoadingStatus(true);
    await sellEnrollment(periodId);
    this.updatedEnrollmentTrigger();
    this.setLoadingStatus(false);
  }

  async deleteEnrollment(periodId: string): Promise<void> {
    this.setLoadingStatus(true);
    await deleteEnrollment(periodId);
    this.updatedEnrollmentTrigger();
    this.setLoadingStatus(false);
  }

  public getPeriodLoading(periodId: string): boolean {
    return this.getActivePeriod(periodId).loading;
  }

  public canConfirmDefaultProposal(periodId: string): boolean {
    try {
      const defaultProposal = this.getDefaultRenewalProposal(periodId);
      return defaultProposal.productLines.every(
        (pLine) => pLine.confirmedPortfolioId === renewalProductLineName(this.rootStore.userStore.sponsorId, pLine.id),
      );
    } catch {
      return false;
    }
  }

  /**
   * Method exist to populate Renewal periods with "Default" Proposal and create Cart for it
   * This happens because during Renewal Trigger process we don't create default cart on CartService and Proposal
   * in the EnrollmentPeriod on EnrollmentService, so we have to create it from the FE.
   *
   * Method does this actions only for the "new" Renewal Periods.
   * Firstly it checks if the period doesn't have "Default" proposals when it's on Renewal stage,
   * that means that it's only moved, so we have to do next steps with it:
   * - create Cart Entity for it - save on CartServiceV2 and use from the response Cart Id to
   * - create System Generated - "Default" Proposal in the Enrollment Period and save it to the EnrollmentService
   * We don't wait for the response from EnrollmentService, so we create Proposal entity and save it to store
   * @private
   */
  private async createCartForDefaultRenewalProposal(): Promise<void> {
    // search for periods to populate with default Proposals
    const newRenewalPeriods = this.activePeriods
      .filter((period) => period.type === EnrollmentType.RENEWAL)
      .filter((period) => !period.proposals.some((proposal) => proposal.createdBy?.id === 'SYSTEM_GENERATED'))
      .filter((period) => period.installedProposal && period.installedProposal.productLines.length > 0);

    this.setLoadingsOnCard(newRenewalPeriods, true);

    // create carts for the proposals
    const cartsToCreate = newRenewalPeriods
      .map((period) => {
        return {
          effectiveDate: period.effectiveDate,
          zipCode: this.group.contactInfo.primaryAddress.zip || '',
          state: this.group.contactInfo.primaryAddress.state || '',
          county: this.group.contactInfo.primaryAddress.county || '',
          shortName: {},
        };
      })
      .map((item) => {
        return this.rootStore.cartStoreV2.createCart(
          this.group.id,
          'AGENT',
          item,
          `Renewal Proposal for ${item.effectiveDate}`,
        );
      });

    const cartPromisesResult = await Promise.all(cartsToCreate);
    await runInAction(async (): Promise<void> => {
      const periodsToPopulateWithProposal: Promise<void>[] = [];

      // save created cart (proposal) for each Enrollment Period
      cartPromisesResult.forEach((result) => {
        const createdCart = (result as FetchResult<{ createCart: Cart }>).data?.createCart;
        if (createdCart) {
          newRenewalPeriods.forEach((period) => {
            this.createDefaultProposal(period, createdCart);
            periodsToPopulateWithProposal.push(this.populateRequestToSaveProposal(period, createdCart));
          });
        }
      });
      await Promise.all(periodsToPopulateWithProposal);
      runInAction(() => {
        this.updatedEnrollmentTrigger();
        this.setLoadingsOnCard(newRenewalPeriods, false);
      });
    });
  }

  private async populateRequestToSaveProposal(period: EnrollmentPeriod, createdCart: Cart): Promise<void> {
    const inputProductLines = period.proposals[0].productLines;

    await saveProposalInEnrollmentService({
      groupId: this.group.id,
      cartName: createdCart.cartName,
      cartId: createdCart.id,
      effectiveDate: createdCart.lastUpdateBrowsingCriteria.effectiveDate,
      productLines: inputProductLines,
      createdBy: this.createdBy(),
    });
  }

  private createDefaultProposal(period: EnrollmentPeriod, createdCart: Cart): void {
    period.proposals.push({
      cartId: createdCart.id,
      name: createdCart.cartName,
      createdOn: createdCart.creationDateTime,
      createdBy: this.createdBy(),
      confirmed: false,
      productLines:
        period.installedProposal?.productLines.map((line) => {
          return {
            id: line.id,
            productType: ProductType.RENEW,
            confirmedPortfolioId: null,
            browsingCriteria: createdCart.lastUpdateBrowsingCriteria,
          };
        }) || [],
      rateSegment: period.installedProposal?.productLines[0]
        ? PRODUCT_LINES_CONTENT_MAP[period.installedProposal.productLines[0].id].rateSegment
        : 'GROUP',
    });
  }

  // eslint-disable-next-line class-methods-use-this
  private createdBy(): InputPerformedBy {
    return {
      id: 'SYSTEM_GENERATED',
      name: 'System Generated',
    };
  }

  public getProductLineFromDefaultRenewalProposal(periodId: string, productLineId: ProductLineId): ProductLine {
    const defaultProposal = this.getDefaultRenewalProposal(periodId);
    const productLine = defaultProposal?.productLines.find((pLine) => pLine.id === productLineId);
    if (productLine) {
      return productLine;
    }
    throw new Error(`No productLine in the Default Proposal with id: ${productLineId}`);
  }

  private getDefaultRenewalProposal(periodId: string): Proposal {
    const defaultProposal = this.getActivePeriod(periodId).proposals.find(
      (proposal) => proposal.createdBy?.id === 'SYSTEM_GENERATED',
    );
    if (defaultProposal) {
      return defaultProposal;
    }
    throw new Error(`No Default Proposal in Enrollment Period with id: ${periodId}`);
  }

  private getActivePeriod(periodId: string): DynamicEnrollmentCard {
    const activePeriod = this.activePeriods.find((period) => period.id === periodId);
    if (activePeriod) {
      return activePeriod;
    }
    throw new Error(`No Period in with id: ${periodId}`);
  }

  // eslint-disable-next-line class-methods-use-this
  private setLoadingsOnCard(periods: DynamicEnrollmentCard[], status: boolean): void {
    // eslint-disable-next-line no-param-reassign,no-return-assign
    periods.forEach((period) => (period.loading = status));
  }

  getGroupLocally(group: Group): void {
    this.group = CopyHelper.deepCopy(group);
  }

  public resetInitialData(): void {
    this.activePeriods = [];
    this.inactivePeriods = [];
    this.enrollmentTrigger = 0;
    this.group = INITIAL_GROUP_OBJECT;
    this.currentPeriod = INITIAL_CURRENT_PERIOD;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.group = INITIAL_GROUP_OBJECT;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
